import { gripVerticalSolidIcon } from "../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * Card widget rendering a title inside a card. In addition, it supports
 * an optional subtitle, icon and footer.
 * For convenience, a default footer can be built by providing a list of
 * actions.
 *
 * @param {string} cssClass - Additional CSS class
 * @param {Renderable} title - Title to render in the card
 * @param {Renderable} [subtitle] - Subtitle
 * @param {Renderable} [banner] - Banner to add at the bottom of the card
 *   content
 * @param {Renderable} [icon] - Icon to add in the middle of the card
 * @param {boolean} [roundedIcon] - When true, the icon is rendered in a circle
 * @param {Renderable[]} [actions] - List of renderables to render as actions in
 *   the footer
 * @param {boolean} [draggable] - True when the card is draggable. This will
 *   append a draggable icon to the top-left corner of the card
 * @param {boolean} [short] - True when the card should be less tall.
 *   This makes the card look nice when it has no footer but cards
 *   around have one: this happens when this card is used as a
 *   trailing card for example
 * @param {Renderable} [footer] - Card footer
 * @param {string} [href] - If provided, the card is an anchor with the
 *   provided `href` as target
 * @param {function} [onClick] - Callback invoked when the card is clicked.
 *   When `href` is provided, this is ignored
 * @param {boolean} [isRestricted] - If true, the card is not clickable
 * @param {string} [dataTestName] - Used to find the widget from a
 * testing tool
 */
export default class CardWidget extends Widget {
	constructor({
		cssClass: className,
		title,
		subtitle,
		banner,
		icon,
		roundedIcon = false,
		actions,
		draggable = false,
		short = false,
		footer,
		href,
		onClick,
		isRestricted = false,
		dataTestName = "card",
	}) {
		super(...arguments);

		this._className = className;
		this._title = title;
		this._subtitle = subtitle;
		this._banner = banner;
		this._icon = icon;
		this._roundedIcon = roundedIcon;
		this._draggable = draggable;
		this._short = short;
		this._footer = footer || this._defaultFooterRenderer({ actions });
		this._href = href;
		this._onClick = onClick;
		this._isClickable = !isRestricted && (href || onClick);
		this._dataTestName = dataTestName;
	}

	renderContentOn(html) {
		const cssClass = [
			"CardWidget",
			this._className,
			{ short: this._short },
		];

		const root = this._href
			? html.a({
					class: [cssClass, { clickable: this._isClickable }],
					href: this._href,
					"data-test": this._dataTestName,
				})
			: html.div({
					class: [cssClass, { clickable: !!this._isClickable }],
					click: this._onClick,
					"data-test": this._dataTestName,
				});

		root.render(
			this._renderCardContentOn.bind(this),
			this._renderFooterContentOn.bind(this),
		);
	}

	_renderCardContentOn(html) {
		html.div(
			{ class: ["card-content", { draggable: this._draggable }] },
			this._renderDraggableIconOn.bind(this),
			this._renderIconOn.bind(this),
			html.div(
				{ class: "card-title" },
				html.h2({ class: "title" }, this._title),
				this._renderSubtitleOn.bind(this),
			),
			this._renderBannerOn.bind(this),
		);
	}

	_renderBannerOn(html) {
		if (!this._banner) {
			return;
		}

		html.div(
			{ class: "banner-container", "data-test": "banner" },
			this._banner,
		);
	}

	_renderDraggableIconOn(html) {
		if (!this._draggable) {
			return;
		}

		html.div(
			{ class: "draggable-icon", "data-test": "drag-handler" },
			gripVerticalSolidIcon,
		);
	}

	_renderIconOn(html) {
		if (!this._icon) {
			return;
		}

		html.div(
			{
				class: ["card-icon", { rounded: this._roundedIcon }],
			},
			this._icon,
		);
	}

	_renderSubtitleOn(html) {
		if (!this._subtitle) {
			return;
		}
		html.h3({ class: "subtitle" }, this._subtitle);
	}

	_renderFooterContentOn(html) {
		if (!this._footer) {
			return;
		}

		html.div({ class: "card-footer" }, this._footer);
	}

	_defaultFooterRenderer({ actions }) {
		if (!actions) {
			return null;
		}
		return (html) => {
			html.div(
				{ class: "footer-actions" },
				html.ul(
					actions.map((action) =>
						html.li({ class: "footer-action" }, action),
					),
				),
			);
		};
	}
}
